import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEO from "@/components/SEO";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";

const contactInfo = [
  {
    icon: Phone,
    title: "טלפון",
    value: "054-572-5681",
    href: "tel:+972545725681",
  },
  {
    icon: Mail,
    title: "אימייל",
    value: "Orbagag@gmail.com",
    href: "mailto:Orbagag@gmail.com",
  },
  {
    icon: MapPin,
    title: "אזור שירות",
    value: "חיפה והקריות",
    href: null,
  },
  {
    icon: Clock,
    title: "שעות פעילות",
    value: "א׳–ה׳ 07:00–18:00 | ו׳ 07:00–13:00",
    href: null,
  },
];

const Contact = () => {
  const heroRef = useScrollReveal();
  const formRef = useScrollReveal();
  const infoRef = useScrollReveal();
  const mapRef = useScrollReveal();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("הטופס נשלח בהצלחה! ניצור איתך קשר בהקדם.");
    setName("");
    setPhone("");
    setEmail("");
    setService("");
    setMessage("");
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="יצירת קשר – גגן מומלץ בחיפה והקריות"
        description="צרו קשר עם אור בגג לתיאום ביקור או הצעת מחיר לתיקון גג רעפים, איטום גגות או פחחות. שירות בחיפה, קריות, נשר, עכו ויקנעם – מענה מהיר."
        path="/contact"
      />
      <Header />
      <main id="main">
        {/* Hero */}
        <section className="pt-24 pb-12 bg-dark" ref={heroRef}>
          <div className="container mx-auto px-4 text-center animate-on-scroll">
            <h1 className="text-3xl md:text-5xl font-bold text-dark-foreground mb-4">
              צרו <span className="text-primary">קשר</span>
            </h1>
            <p className="text-dark-foreground/70 text-lg max-w-2xl mx-auto">
              השאירו פרטים ונחזור אליכם תוך שעות ספורות, או פנו אלינו ישירות בכל אחד מהערוצים
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Form - 3 cols */}
              <div className="lg:col-span-3" ref={formRef}>
                <Card className="border-border shadow-lg animate-on-scroll">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-bold text-foreground mb-6">
                      טופס <span className="text-primary">פנייה</span>
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">שם מלא *</label>
                          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="הזינו את שמכם" required />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">טלפון *</label>
                          <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="054-572-5681" required dir="ltr" className="text-right" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">אימייל</label>
                          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" dir="ltr" className="text-right" />
                        </div>
                        <div>
                          <label htmlFor="service" className="block text-sm font-medium text-foreground mb-1">סוג שירות</label>
                          <Select value={service} onValueChange={setService}>
                            <SelectTrigger id="service" aria-label="סוג שירות">
                              <SelectValue placeholder="בחרו שירות" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="leaks">איתור ותיקון נזילות</SelectItem>
                              <SelectItem value="construction">בנייה קלה</SelectItem>
                              <SelectItem value="gutters">מרזבים</SelectItem>
                              <SelectItem value="metal">פחחות</SelectItem>
                              <SelectItem value="ventilation">אוורור גגות</SelectItem>
                              <SelectItem value="consulting">ייעוץ מקצועי</SelectItem>
                              <SelectItem value="other">אחר</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1">הודעה</label>
                        <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="ספרו לנו על הפרויקט שלכם..." rows={4} />
                      </div>
                      <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-gold-light font-bold text-lg py-6">
                        <Send size={18} />
                        שלחו פנייה
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Info - 2 cols */}
              <div className="lg:col-span-2 space-y-6" ref={infoRef}>
                <div className="animate-on-scroll">
                  <h2 className="text-2xl font-bold text-foreground mb-6">
                    פרטי <span className="text-primary">התקשרות</span>
                  </h2>
                  <div className="space-y-4">
                    {contactInfo.map((item) => (
                      <Card key={item.title} className="border-border hover:border-primary/40 transition-colors">
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <item.icon className="text-primary" size={22} />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">{item.title}</p>
                            {item.href ? (
                              <a href={item.href} className="text-foreground font-medium hover:text-primary transition-colors" dir="ltr">
                                {item.value}
                              </a>
                            ) : (
                              <p className="text-foreground font-medium">{item.value}</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <div className="animate-on-scroll" style={{ transitionDelay: "200ms" }}>
                  <Card className="border-primary/30 bg-dark">
                    <CardContent className="p-6 text-center">
                      <MessageCircle className="text-primary mx-auto mb-3" size={36} />
                      <h3 className="text-lg font-bold text-dark-foreground mb-2">מעדיפים WhatsApp?</h3>
                      <p className="text-dark-foreground/70 text-sm mb-4">שלחו לנו הודעה ונחזור אליכם במהירות</p>
                      <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-gold-light font-bold">
                        <a href="https://wa.me/972545725681" target="_blank" rel="noopener noreferrer">
                          <MessageCircle size={18} />
                          פנייה ב-WhatsApp
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-12 bg-muted" ref={mapRef}>
          <div className="container mx-auto px-4 animate-on-scroll">
            <h2 className="text-2xl font-bold text-foreground text-center mb-8">
              אזור <span className="text-primary">השירות</span>
            </h2>
            <div className="max-w-5xl mx-auto rounded-xl overflow-hidden shadow-lg border border-border">
              <iframe
                title="אזור שירות - אור בגג"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d215000!2d34.9896!3d32.7940!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151db4b9c1d7a6df%3A0xc6076ad9b8a6b8d8!2sHaifa!5e0!3m2!1siw!2sil!4v1"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Contact;
